/**
 * useFleetEngine — el motor de datos de la flota.
 *
 * Es el antiguo `useDevices` con una sola diferencia estructural: no guarda
 * NADA en `useState`. Todo lo que antes era estado de React (devices, traces,
 * comandos, syncRequests, alertas de geocerca, loading) ahora vive
 * en un `FleetStore` que se le pasa por parámetro.
 *
 * Eso es lo que permite montar el motor en un provider sin que el provider se
 * re-renderice: un mensaje WS muta el store y despierta SOLO a los
 * suscriptores del IMEI afectado. El árbol por encima no se entera.
 *
 * La semántica de sincronización (guards de orden, merge REST/WS versionado,
 * replay de buffer offline, polling de seguridad, resync de reconexión) es la
 * misma que venía de las fases anteriores y está documentada en cada bloque.
 * Un detalle SÍ cambia y para mejor: el store es síncrono, así que la cabeza
 * de la traza que leen los guards siempre está fresca — ya no hay la ventana
 * "el ref se refresca post-commit" que obligaba a duplicar el guard.
 */

import { useCallback, useEffect, useRef } from "react";
import { api } from "../api/client.js";
import { useWebSocket } from "./useWebSocket.js";
import { createWsLogger } from "../lib/wsDebugLog.js";
import { haversine } from "../lib/leaflet/animation/distance.js";
import {
  SAME_LOC,
  dedupAdjacentSameLoc,
} from "../lib/leaflet/animation/dedup.js";
import { fetchPositionsBatched } from "../lib/fleet/positionsBatch.js";
import { isHiddenVehicleStatus } from "../utils/deviceState.js";
import { evaluateSyncTrigger } from "../lib/fleet/syncTrigger.js";
import {
  SYNC_DISTANCE_THRESHOLD_M,
  SYNC_LIVE_GAP_THRESHOLD_MS,
} from "../config/syncAnimation.js";

export { evaluateSyncTrigger };

/**
 * Safety polling fallback — fires when WS goes silent.
 *
 * If no WS message is received for WS_SILENCE_THRESHOLD_MS the engine
 * periodically fetches GET /api/devices to keep ignition, charging,
 * last_seen_at, status, and relay_status fresh.
 *
 * Polling is suppressed while the tab is hidden (document.hidden) and stops
 * automatically as soon as WS activity resumes.
 *
 * Only state-level fields are merged; position/trace fields are never touched
 * by the fallback poll.
 */
/** How long (ms) with no WS activity before we consider the connection unhealthy. */
export const WS_SILENCE_THRESHOLD_MS = 60_000; // 60 s
/** How often (ms) to poll while the WS appears silent. */
export const POLL_INTERVAL_MS = 30_000; // 30 s

/**
 * Máximo entre dos timestamps donde cualquiera puede ser NaN/undefined (los
 * no-finitos se ignoran; si ninguno es finito devuelve el primero, NaN).
 */
function maxFiniteTs(a, b) {
  if (Number.isFinite(a) && Number.isFinite(b)) return Math.max(a, b);
  return Number.isFinite(b) ? b : a;
}

/**
 * Máximo de alertas de geocerca vivas a la vez (las más viejas se descartan).
 * Dimensionado para que un LOTE coalescido completo quepa entero: el efecto de
 * toasts de MonitorPage solo ve el array publicado, así que un cap menor que
 * la ráfaga de un flush perdería cruces sin toast. La pantalla igual satura en
 * MAX_VISIBLE_TOASTS (8) — este número solo debe cubrir el transporte.
 */
const MAX_GEOFENCE_ALERTS = 50;

/**
 * Fields refreshed by the safety poll. Deliberately excludes position fields
 * (lat, lng, speed, heading, …) — those come only from WS messages.
 *
 * Telemetry fields (voltage_level, signal_bars, satellites) are included
 * because the safety poll is the ONLY recovery path when the WS connection
 * is silent: WS_RT_FIELDS protects them from being clobbered by the initial
 * HTTP fetch, but the poll bypass uses this list to refresh them from the
 * authoritative DB state.
 */
const POLL_FIELDS = [
  "ignition",
  "charging",
  "last_seen_at",
  "status",
  "relay_status",
  "voltage_level",
  "signal_bars",
  "satellites",
  "last_ignition_at",
  "last_charging_at",
  // Estado de movimiento: tiene que viajar con `ignition` o el poll reproduce la
  // lectura contradictoria. Si la WS se calla sin disparar onReconnect (cold
  // start de Fly, LTE flojo), el poll refrescaba la ignición a false y dejaba el
  // movement_status de memoria en 'moving' → "En movimiento 45 km/h · Motor
  // apagado" hasta que hubiera una reconexión de verdad. `speed` va también:
  // alimenta el label `En movimiento ${speed} km/h`.
  "movement_status",
  "stopped_since",
  "parked_since",
  // speed y heading viajan juntos: el label usa speed y el marcador rota con
  // heading; refrescar uno solo deja el ícono apuntando a donde iba antes.
  "speed",
  "heading",
  // Odómetro: si la WS cae mientras el vehículo se mueve, el poll de seguridad
  // es la única vía para refrescar la distancia acumulada desde el DB.
  "total_distance_km",
  "total_distance_since",
  // Estado administrativo del vehículo. Va en el poll porque manda sobre el de
  // telemetría: sin él, marcar un vehículo como robado no se veía en el Monitor
  // mientras la WebSocket estuviera callada. El trigger de la migración 118
  // mueve devices.updated_at, así que el delta `?since=` lo trae.
  "vehicle_status",
];

const wsLog = createWsLogger("[ws-resync]");

/**
 * Device-side resync (replay de buffer offline).
 *
 * Cuando un dispositivo GPS recupera señal re-sube su buffer offline y el
 * backend re-broadcastea cada posición con `is_live: false` (timestamp GPS
 * viejo). Esas posiciones NO mueven el marcador ni entran al trail: el
 * recorrido del período desconectado no se dibuja (misma decisión de producto
 * que el resync de reconexión frontend, PR #535). Solo se aplica telemetría y
 * se recuerda la más reciente; si el replay termina sin que llegue una
 * posición live (el device vuelve a dormirse tras descargar el buffer), el
 * flush diferido aplica esa última posición para no dejar el marcador
 * congelado en la posición pre-desconexión.
 */
export const REPLAY_FLUSH_DELAY_MS = 3_000;

// Re-fetch de /api/devices post-reconnect: timeout de 10s por intento + 1
// retry. Sin esto, un fetch colgado deja el overlay "Sincronizando" bajando
// en .finally() sin que la data se haya refrescado (fallo silencioso).
const RESYNC_FETCH_TIMEOUT_MS = 10_000;
const RESYNC_RETRY_DELAY_MS = 1_000;

/**
 * Hasta esta cantidad de vehículos, la siembra de trazas es completa para toda
 * la flota — el comportamiento de siempre, sin partes móviles nuevas.
 *
 * Por encima se siembra por niveles (una posición para todos, cola solo para
 * el viewport). El número está puesto donde el pedido completo deja de ser
 * gratis: 300 vehículos × 40 filas crudas ya son 12.000 filas por carga.
 */
const THIN_SEED_THRESHOLD = 300;

async function getDevicesWithRetry(
  apiFn,
  { timeoutMs = RESYNC_FETCH_TIMEOUT_MS, retries = 1, signal = null } = {},
) {
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    if (signal?.aborted) {
      throw signal.reason instanceof Error
        ? signal.reason
        : new DOMException("Resync aborted", "AbortError");
    }
    // El timeout por intento lo aplica `request()` en api/client.js, que ya
    // encadena señal externa + deadline propio y aborta con un TimeoutError
    // que dice qué path expiró. Rearmar acá ese encadenado duplicaba el valor
    // del deadline en dos módulos y perdía la razón del abort en el log.
    try {
      return await apiFn({ signal, timeoutMs });
    } catch (err) {
      // Un abort externo no se reintenta: el vuelo entero fue cancelado.
      if (signal?.aborted || attempt === retries) throw err;
      wsLog("devices-refetch retry after fail", { error: err?.message ?? String(err) });
      await new Promise((r) => setTimeout(r, RESYNC_RETRY_DELAY_MS));
    }
  }
}

// Corta un trace (newest-first) en el hueco offline más reciente: si entre
// dos puntos consecutivos hay un gap GPS > SYNC_LIVE_GAP_THRESHOLD_MS con
// desplazamiento ≥ SYNC_DISTANCE_THRESHOLD_M, el trail conserva solo el
// tramo continuo más nuevo. Sin este corte, el seed HTTP de initTraces dibuja
// el segmento del período desconectado como línea recta cruzando el mapa — el
// hueco vive en el historial de positions, así que los guards del camino WS
// (is_live, colapso de #535) no lo cubren. Mismos umbrales y misma semántica
// que el sync live-gap: el trail re-arranca desde la reconexión.
export function truncateTraceAtGap(points) {
  if (!Array.isArray(points)) return [];
  for (let i = 0; i + 1 < points.length; i += 1) {
    const newer = points[i];
    const older = points[i + 1];
    const newerTs = newer?.timestamp ? Date.parse(newer.timestamp) : NaN;
    const olderTs = older?.timestamp ? Date.parse(older.timestamp) : NaN;
    if (!Number.isFinite(newerTs) || !Number.isFinite(olderTs)) continue;
    if (
      newerTs - olderTs > SYNC_LIVE_GAP_THRESHOLD_MS &&
      Number.isFinite(newer.lat) &&
      Number.isFinite(newer.lng) &&
      Number.isFinite(older.lat) &&
      Number.isFinite(older.lng) &&
      haversine(newer.lat, newer.lng, older.lat, older.lng) >=
        SYNC_DISTANCE_THRESHOLD_M
    ) {
      return points.slice(0, i + 1);
    }
  }
  return points;
}

/**
 * Cuántas filas crudas pedir al seed HTTP para poder armar una cola de N
 * posiciones DISTINTAS. Los heartbeats estacionados ocupan las últimas filas
 * crudas de un vehículo recién estacionado; con limit = N la ruta real ya no
 * viene en la respuesta y no hay nada que dedupear.
 */
export function seedFetchLimit(count) {
  const n = Number.isFinite(count) && count > 0 ? count : 30;
  // Piso de 40: con reportes cada ~20 s cubre >10 min de heartbeats, de sobra
  // para alcanzar la ruta de un recién estacionado. Deliberadamente conservador
  // — este mismo fetch corre en cada reconexión y vuelta de pestaña, que es el
  // camino más expuesto a los cold starts de Fly, y un timeout ahí deja al
  // vehículo sin trail hasta que la WS lo rellene.
  return Math.min(Math.max(n * 4, 40), 200);
}

/**
 * Arma un trace listo para pintar a partir de filas crudas newest-first:
 * corta el hueco offline, colapsa las rachas mismo-lugar y recién ahí capa a
 * `count`. El orden importa: el corte de hueco necesita la adyacencia real de
 * timestamps (es la que define el hueco), y el cap tiene que ser el último
 * paso para que `count` cuente posiciones DISTINTAS y no heartbeats.
 *
 * Devuelve SIEMPRE un array nuevo — nunca la referencia de entrada — para que
 * el resultado se pueda guardar en el store sin alias con las filas crudas.
 *
 * OJO al alimentarla: `points` tiene que traer las filas CRUDAS, no un trace ya
 * colapsado. En un trace colapsado, la cabeza estacionada y el punto de llegada
 * pueden quedar adyacentes con >2 min y >50 m entre medio — la firma exacta de
 * un hueco offline — y el corte los separaría borrando el tramo de llegada. Por
 * eso las ramas de union de `initTraces` mezclan la memoria con las filas
 * crudas del fetch: restauran la adyacencia real de timestamps antes de que
 * corra el corte.
 *
 * @param {Array<{lat: number, lng: number, timestamp: string}>} points newest-first (crudas)
 * @param {number} [count] cap de posiciones distintas; sin él, no capa
 * @returns {Array} new array
 */
export function buildTrace(points, count) {
  const deduped = dedupAdjacentSameLoc(truncateTraceAtGap(points));
  return count ? deduped.slice(0, count) : deduped.slice();
}

/**
 * Real-time fields maintained by WebSocket messages.
 * When the initial HTTP fetch resolves after a WS update has already applied
 * fresher state, these fields are preserved from the existing store entry
 * rather than being overwritten by the (possibly stale) HTTP snapshot.
 */
const WS_RT_FIELDS = [
  "lat",
  "lng",
  "speed",
  "heading",
  "satellites",
  "last_position_at",
  "gps_received_at",
  "movement_status",
  "stopped_since",
  "parked_since",
  "has_gps_fix",
  "voltage_level",
  "signal_bars",
  "mcc",
  "mnc",
  "lac",
  "cell_id",
  "last_seen_at",
  "ignition",
  "charging",
  "relay_status",
  "status",
  "last_ignition_at",
  "last_charging_at",
  // Odómetro acumulado en vivo por los mensajes WS position → protegerlo de que
  // el fetch HTTP inicial (más viejo) lo pise con un valor menor.
  "total_distance_km",
  "total_distance_since",
];

/** Return a shallow copy of `obj` restricted to the provided keys. */
function pick(obj, keys) {
  const out = {};
  for (const k of keys) {
    if (k in obj) out[k] = obj[k];
  }
  return out;
}

/**
 * Mantiene el `FleetStore` al día: fetch inicial, mensajes WS, resync de
 * reconexión y polling de seguridad.
 *
 * @param {object} store           FleetStore creado con `createFleetStore()`
 * @param {object} options
 * @param {number} options.tracePositions  Cuántas posiciones conservar por trail
 * @returns {void} Todo lo que produce va al store.
 */
export function useFleetEngine(store, { tracePositions = 30 } = {}) {
  // Keep tracePositions in a ref so the WS handler always reads the latest value
  const tracePositionsRef = useRef(tracePositions);
  useEffect(() => {
    tracePositionsRef.current = tracePositions;
  }, [tracePositions]);

  const storeRef = useRef(store);
  useEffect(() => {
    storeRef.current = store;
  }, [store]);

  // Track whether initial device fetch has completed
  const hasFetchedRef = useRef(false);
  // Distinto de hasFetchedRef: se marca aunque el fetch inicial FALLE. Lo usa
  // el handler de device_status para decidir si un IMEI desconocido puede
  // crear una fila provisional. Con hasFetchedRef (que solo se setea en el
  // camino feliz) un blip de red dejaba esa puerta abierta el resto de la
  // sesión, que es justo lo que hay que cerrar.
  const initialFetchSettledRef = useRef(false);
  // `handleWsReconnect`, al alcance de `handleWsMessage` (que se declara antes)
  // para traer de vuelta un vehículo que deja de estar robado.
  const resyncRef = useRef(null);
  // Contador monótono de siembras. Solo genera identificadores únicos; quien
  // decide si una respuesta sigue vigente es el sello POR IMEI de abajo.
  const fetchVersionRef = useRef(0);

  // IMEI → versión de la siembra que lo pidió por última vez.
  //
  // El guard de respuesta obsoleta NO puede ser un único contador global. Las
  // siembras ya no son una a la vez ni cubren lo mismo: la fina (una posición
  // para toda la flota) convive con la del viewport (cola completa para lo que
  // está en pantalla), y ambas se disparan en el mismo segundo al cargar el
  // Monitor. Con un contador global, la segunda invalidaba a la primera entera
  // y la flota se quedaba sin coordenadas — `/api/devices` las stripea, así que
  // esa siembra era la única fuente, y sin lat/lng un vehículo tampoco puede
  // entrar nunca al viewport que lo rescataría.
  //
  // Con el sello por IMEI cada respuesta publica solo los vehículos que nadie
  // volvió a pedir mientras tanto, que es lo que el guard siempre quiso decir.
  const traceSeedVersionRef = useRef(new Map());

  // IMEIs con una siembra de cola EN VUELO. Evita que un pan que los vuelva a
  // meter en el viewport dispare un segundo request por el mismo dato.
  const pendingSeedRef = useRef(new Set());

  // Per-device WS version: monotonic JS timestamp (Date.now()) of the last
  // WS message that mutated a device. The initial HTTP fetch uses this to
  // detect the REST/WS race: if WS arrived while the fetch was in flight we
  // preserve WS real-time fields instead of overwriting them with HTTP data.
  const deviceWsVersionRef = useRef(new Map());

  // Versión acotada al ESTADO DE MOVIMIENTO. La de arriba la estampa cualquier
  // mensaje, incluido un `telemetry` de heartbeat que no trae movement_status ni
  // speed. Como el merge de reconexión preserva de memoria todo un grupo de
  // campos cuando la versión es más nueva, usar la versión general fijaba el
  // estado viejo por culpa de un heartbeat que no dice nada del movimiento — y
  // estos trackers emiten cada ~1-2.5 min, así que caía dentro de la ventana del
  // refetch bastante seguido. Sólo la estampan los mensajes que SÍ traen estado
  // de movimiento: `position` y `device_movement`.
  const deviceMovementWsVersionRef = useRef(new Map());

  // Timestamp GPS (ms) de la última posición APLICADA por imei. El store es
  // síncrono, así que la cabeza de la traza ya está fresca dentro de un lote
  // coalescido; este Map se conserva porque también cubre el camino del flush
  // diferido de replay, donde el punto pendiente aún no tocó el store.
  const latestAppliedPosTsRef = useRef(new Map());

  // Versión acotada a `status`. device_status solo trae {imei, status}: si
  // estampara la versión general protegería TODO el grupo de telemetría en los
  // merges; si no estampara nada, un status emitido mientras el fetch inicial
  // está en vuelo sería pisado por el snapshot viejo — y un device offline no
  // vuelve a emitir, así que quedaría mal pintado hasta el próximo resync.
  const deviceStatusWsVersionRef = useRef(new Map());

  // ── Safety polling state ──────────────────────────────────────────────────
  const lastWsActivityAtRef = useRef(Date.now());
  const pollingActiveRef = useRef(false);
  const pollIntervalRef = useRef(null);
  const currentPollController = useRef(null);
  const watchdogIntervalRef = useRef(null);
  const pendingGeofenceTimers = useRef(new Set());

  // IMEIs cuya traza ya se sembró COMPLETA (con `tracePositions` posiciones).
  // Los que solo tienen la siembra fina de una posición no cuentan: sirven
  // para pintar el marcador, no para pintar la cola.
  const fullySeededRef = useRef(new Set());

  /**
   * Poda de los Maps auxiliares por IMEI. Los handlers WS estampan entradas
   * incluso para IMEIs que luego se rechazan, y nada las borraba —
   * crecimiento monótono en sesiones largas con rotación de dispositivos.
   * Se llama con la flota COMPLETA: lo que no está ahí ya no existe.
   */
  const pruneByFleet = useCallback((allImeis) => {
    const fleetImeis = new Set(allImeis);
    for (const map of [
      deviceWsVersionRef.current,
      deviceMovementWsVersionRef.current,
      deviceStatusWsVersionRef.current,
      latestAppliedPosTsRef.current,
    ]) {
      for (const key of map.keys()) {
        if (!fleetImeis.has(key)) map.delete(key);
      }
    }
    for (const imei of fullySeededRef.current) {
      if (!fleetImeis.has(imei)) fullySeededRef.current.delete(imei);
    }
    for (const imei of traceSeedVersionRef.current.keys()) {
      if (!fleetImeis.has(imei)) traceSeedVersionRef.current.delete(imei);
    }
  }, []);

  /**
   * Load initial traces — un solo lote (troceado internamente) para los IMEIs
   * pedidos. Mezcla por timestamp con lo que ya hay en el store, así que los
   * mensajes WS que aterrizaron mientras el request estaba en vuelo NO se
   * pisan.
   *
   * `onMerged({ prev, next })` corre sincrónicamente con el mapa pre-merge y
   * post-merge. Si devuelve un objeto, ese objeto REEMPLAZA al mapa mezclado —
   * handleWsReconnect lo usa para colapsar la traza de los devices que
   * dispararon sync.
   *
   * Todo el merge ocurre en JS plano (fuera de React) y se publica al store en
   * un solo `batch`.
   */
  const initTraces = useCallback(async (imeis, count, onMerged, options = {}) => {
    const fleetStore = storeRef.current;
    if (!fleetStore || imeis.length === 0) return [];

    const version = ++fetchVersionRef.current;
    for (const imei of imeis) traceSeedVersionRef.current.set(imei, version);

    // `seedFetchLimit` pide filas crudas de más porque los heartbeats de un
    // vehículo estacionado ocupan las últimas y hay que llegar a la ruta real.
    // La siembra fina no necesita nada de eso: quiere UNA fila, la más nueva,
    // que es la posición actual. `rawLimit` la deja pedir exactamente eso.
    const rawLimit = options.rawLimit ?? seedFetchLimit(count);
    const signal = options.signal ?? null;

    let batchResult = {};
    try {
      batchResult = await fetchPositionsBatched(imeis, rawLimit, { signal });
    } catch (err) {
      // Timeout/abort is non-fatal — WS will backfill traces as positions arrive.
      // Downgrade to warn so cold-start or large-fleet hiccups don't spam red.
      if (err?.name === "TimeoutError" || err?.name === "AbortError") {
        console.warn(
          "[fleetEngine] initTraces batch aborted (timeout); WS will backfill:",
          err.message,
        );
      } else {
        console.error("[fleetEngine] initTraces batch failed:", err);
      }
    }

    // Vuelo cancelado (resync superseded / unmount) — no publicar nada al
    // store; el dueño nuevo del resync trae su propia siembra.
    if (signal?.aborted) return [];

    // Solo se publican los IMEIs que nadie volvió a pedir mientras este fetch
    // estaba en vuelo. Una siembra posterior con OTRO alcance ya no descarta
    // esta: cada vehículo se resuelve por su cuenta.
    const owned = imeis.filter(
      (imei) => traceSeedVersionRef.current.get(imei) === version,
    );
    if (owned.length === 0) return [];

    // ── Merge fuera de React ────────────────────────────────────────────────
    const prevTraces = {};
    const merged = {};
    for (const imei of owned) {
      const memory = fleetStore.getTrace(imei);
      prevTraces[imei] = memory;
      const raw = batchResult[imei] || [];
      // API returns oldest→newest; reverse so index 0 = most recent.
      const fetched = raw
        .slice()
        .reverse()
        .map((p) => ({
          ...p,
          speed: p.speed ?? 0,
          movement_status: (p.speed ?? 0) > 2 ? "moving" : "stopped",
        }));

      // ── Timestamp-aware merge ─────────────────────────────────────────
      // If memory has a point newer than the fetched newest, WS already
      // delivered a fresh position during the fetch → keep memory.
      // Otherwise, union by timestamp and sort newest-first, cap at count.
      if (fetched.length === 0) {
        // Fetch brought nothing → don't wipe memory.
        merged[imei] = memory;
        continue;
      }
      if (memory.length === 0) {
        merged[imei] = buildTrace(fetched, count);
        continue;
      }

      const memoryNewestTs = memory[0]?.timestamp;
      const fetchedNewestTs = fetched[0]?.timestamp;
      if (
        memoryNewestTs &&
        fetchedNewestTs &&
        new Date(memoryNewestTs).getTime() > new Date(fetchedNewestTs).getTime()
      ) {
        // Memory has newer WS data — union but prefer memory on conflict.
        const seen = new Set(memory.map((p) => p.timestamp));
        merged[imei] = buildTrace(
          [...memory, ...fetched.filter((p) => !seen.has(p.timestamp))].sort(
            (a, b) =>
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
          ),
          count,
        );
        continue;
      }

      // Fetched is newest — union but cap.
      const seen = new Set(fetched.map((p) => p.timestamp));
      merged[imei] = buildTrace(
        [...fetched, ...memory.filter((p) => !seen.has(p.timestamp))].sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        ),
        count,
      );
    }

    let published = merged;
    if (typeof onMerged === "function") {
      try {
        const adjusted = onMerged({ prev: prevTraces, next: merged });
        if (adjusted && typeof adjusted === "object") published = adjusted;
      } catch (err) {
        console.error("[fleetEngine] initTraces onMerged callback failed:", err);
      }
    }

    fleetStore.batch(() => {
      for (const imei of Object.keys(published)) {
        fleetStore.setTrace(imei, published[imei]);
      }

      // Sync device lat/lng with the most recent merged point. Solo se pisa si
      // el device no tiene ya una posición MÁS NUEVA (un mensaje WS pudo
      // aterrizar entre el fetch y esta publicación).
      for (const imei of Object.keys(published)) {
        const device = fleetStore.getDevice(imei);
        if (!device) continue;
        const newest = published[imei]?.[0];
        if (!newest || newest.lat == null || newest.lng == null) continue;
        if (
          device.last_position_at &&
          new Date(device.last_position_at).getTime() >
            new Date(newest.timestamp).getTime()
        ) {
          continue;
        }
        fleetStore.patchDevice(imei, {
          lat: newest.lat,
          lng: newest.lng,
          last_position_at: newest.timestamp,
        });
      }
    });

    // IMEIs que el backend RESPONDIÓ, no los que se pidieron. La distinción
    // importa: `fetchPositionsBatched` se traga el fallo de un chunk y devuelve
    // `{}`, indistinguible a simple vista de "este vehículo no tiene
    // posiciones". La respuesta sí los distingue — el endpoint devuelve una
    // clave por cada IMEI visible, con array vacío si no hay filas —, así que
    // un chunk caído no deja a esos vehículos marcados como sembrados y el
    // próximo viewport los reintenta.
    return owned.filter((imei) => imei in batchResult);
  }, []);

  /**
   * Siembra la cola de `imeis` y marca como sembrados SOLO los que se
   * publicaron de verdad. Mientras el request está en vuelo quedan en
   * `pendingSeedRef` para que un pan que los vuelva a cruzar no pida lo mismo
   * dos veces; si la respuesta se pierde (timeout, cold start) salen de ahí sin
   * marcar y el próximo viewport los reintenta.
   */
  const seedTrailsFor = useCallback(
    async (imeis, count, onMerged, options = {}) => {
      if (imeis.length === 0) return [];
      for (const imei of imeis) pendingSeedRef.current.add(imei);
      try {
        const seeded = await initTraces(imeis, count, onMerged, options);
        for (const imei of seeded) fullySeededRef.current.add(imei);
        return seeded;
      } finally {
        for (const imei of imeis) pendingSeedRef.current.delete(imei);
      }
    },
    [initTraces],
  );

  /**
   * Siembra inicial de trazas, por niveles.
   *
   * Las trazas cumplen DOS funciones distintas y por eso no se pueden recortar
   * de cualquier manera:
   *
   *   1. Dan la posición del marcador. `/api/devices` no trae lat/lng (se
   *      stripean a propósito: la tabla `positions` es la fuente única), así
   *      que sin este seed un vehículo no aparece en el mapa hasta que la WS
   *      entregue una posición — hasta un par de minutos en uno estacionado.
   *      Esto lo necesita TODA la flota.
   *   2. Dibujan la cola. Eso solo lo necesita lo que está en pantalla.
   *
   * Con flotas chicas se pide todo completo y listo. Pasado el umbral se
   * separan: una fila por vehículo para que todos los marcadores aparezcan ya,
   * y la cola completa solo para el viewport que haya declarado el mapa. El
   * resto de las colas llegan cuando el usuario paneé hasta ellas (efecto de
   * `subscribeTraceInterest`).
   */
  const seedInitialTraces = useCallback(
    async (allImeis) => {
      const fleetStore = storeRef.current;
      pruneByFleet(allImeis);
      const count = tracePositionsRef.current;

      if (allImeis.length <= THIN_SEED_THRESHOLD) {
        await seedTrailsFor(allImeis, count);
        return;
      }

      // Una posición por vehículo: barato y suficiente para pintar la flota.
      await initTraces(allImeis, 1, undefined, { rawLimit: 1 });

      const interest = fleetStore.getTraceInterest();
      if (!interest) return; // nadie declaró viewport todavía
      const wanted = allImeis.filter((imei) => interest.has(imei));
      await seedTrailsFor(wanted, count);
    },
    [initTraces, pruneByFleet, seedTrailsFor],
  );

  /**
   * Re-siembra tras un resync (reconexión o vuelta de pestaña).
   *
   * Mismo criterio por niveles que la siembra inicial, con una diferencia: acá
   * la mayoría de la flota YA tiene posición en memoria (el merge del roster la
   * preserva), así que la siembra fina solo hace falta para los vehículos que
   * aparecieron durante la caída.
   *
   * `onMerged` (la evaluación de disparos de sync) va con la pasada COMPLETA:
   * comparar el punto anterior con el nuevo solo tiene sentido donde había cola.
   */
  const seedAfterResync = useCallback(
    async (allImeis, onMerged, options = {}) => {
      const fleetStore = storeRef.current;
      pruneByFleet(allImeis);
      const count = tracePositionsRef.current;

      if (allImeis.length <= THIN_SEED_THRESHOLD) {
        await seedTrailsFor(allImeis, count, onMerged, options);
        return;
      }

      const withoutPosition = allImeis.filter(
        (imei) => fleetStore.getTrace(imei).length === 0,
      );
      if (withoutPosition.length > 0)
        await initTraces(withoutPosition, 1, undefined, {
          rawLimit: 1,
          signal: options.signal,
        });

      const interest = fleetStore.getTraceInterest();
      const full = allImeis.filter(
        (imei) =>
          fullySeededRef.current.has(imei) || (interest && interest.has(imei)),
      );
      await seedTrailsFor(full, count, onMerged, options);
    },
    [initTraces, pruneByFleet, seedTrailsFor],
  );

  // Reloj de deltas para /api/devices?since=: max(updated_at) visto en
  // cualquier respuesta de la ruta (fetch inicial, resync, poll). El poll de
  // seguridad lo manda como `since` y el server devuelve solo lo que cambió;
  // una respuesta vacía significa "sin cambios" y no avanza el reloj. Se
  // guarda en ms epoch (comparación numérica, inmune a formatos de TZ).
  const maxUpdatedAtRef = useRef(null);
  // El cursor se manda retrasado esta ventana. `updated_at` lo estampa NOW(),
  // que en Postgres es la hora de INICIO de la transacción: una escritura de
  // ingesta que arrancó antes de nuestra consulta pero commitea después queda
  // con un updated_at MENOR que el máximo que vimos. Con un `since` exacto ese
  // device no volvería a entrar en ningún delta posterior (el filtro es
  // `> since`) y quedaría congelado hasta el próximo resync. Solapar cubre
  // cualquier transacción más corta que la ventana; el costo es re-enviar las
  // filas tocadas en el último minuto, que es exactamente lo que el poll
  // querría ver de todos modos.
  //
  // El tamaño importa: por encima de POLL_INTERVAL_MS (30 s) cada fila tocada
  // reaparecería en DOS ticks seguidos y el delta no recortaría nada en la
  // flota activa, que es donde el ahorro cuenta. 15 s es holgado frente a las
  // transacciones de ingesta (un statement, sub-segundo) y deja que un device
  // entre una sola vez por ciclo.
  const DELTA_OVERLAP_MS = 15_000;
  const trackUpdatedAt = useCallback((deviceList) => {
    for (const d of deviceList) {
      const t = d?.updated_at ? Date.parse(d.updated_at) : NaN;
      if (Number.isFinite(t) && t > (maxUpdatedAtRef.current ?? -Infinity)) {
        maxUpdatedAtRef.current = t;
      }
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    const fleetStore = storeRef.current;
    fleetStore.setStatus({ loading: true });
    // Record the fetch start time so we can detect WS messages that arrived
    // after the fetch began (and therefore carry newer state than the response).
    const fetchStartedAt = Date.now();
    api
      .getDevices()
      .then((deviceList) => {
        trackUpdatedAt(deviceList);
        // Strip stale/invalid lat/lng from API response — positions endpoint is the
        // single source of truth. The map guards on null, so no marker renders until
        // initTraces provides the correct position from the positions table.
        // El backend ya no sirve los robados en el snapshot completo; el
        // filtro repetido acá es por si un backend viejo los mandara.
        const devicesWithoutPos = deviceList
          .filter((d) => !isHiddenVehicleStatus(d.vehicle_status))
          .map(
            // eslint-disable-next-line no-unused-vars
            ({ lat, lng, last_position_at, ...rest }) => rest,
          );
        hasFetchedRef.current = true;
        // Version-aware merge: if WS messages arrived while the fetch was in
        // flight, preserve their real-time fields rather than overwriting them
        // with the (potentially stale) HTTP snapshot.
        fleetStore.setRoster(devicesWithoutPos, (fresh, existing) => {
          if (!existing) return fresh;
          const wsVersion = deviceWsVersionRef.current.get(fresh.imei);
          const statusVersion = deviceStatusWsVersionRef.current.get(fresh.imei);
          const wsNewer = wsVersion !== undefined && wsVersion >= fetchStartedAt;
          const statusNewer =
            statusVersion !== undefined && statusVersion >= fetchStartedAt;
          if (!wsNewer && !statusNewer) return fresh;
          // WS data is newer — keep existing real-time fields, adopt fresh
          // metadata (name, plate, driver, relay_status, etc.) from HTTP.
          const merged = wsNewer
            ? { ...fresh, ...pick(existing, WS_RT_FIELDS) }
            : { ...fresh };
          // `status` tiene versión propia: un device_status (solo {imei,
          // status}) emitido con el fetch en vuelo debe sobrevivir al snapshot
          // viejo sin proteger de paso el resto de la telemetría.
          if (statusNewer && existing.status !== undefined) {
            merged.status = existing.status;
          }
          return merged;
        });
        return seedInitialTraces(devicesWithoutPos.map((d) => d.imei));
      })
      .catch((err) => console.error("[fleetEngine] fetch failed:", err))
      .finally(() => {
        initialFetchSettledRef.current = true;
        storeRef.current?.setStatus({ loading: false });
      });
  }, [seedInitialTraces, trackUpdatedAt]);

  // Re-fetch traces from API when tracePositions changes (not on initial mount).
  // Solo los que ya tenían la cola completa: subir el ajuste no debería
  // desencadenar la siembra de una flota entera que nadie está mirando.
  useEffect(() => {
    if (!hasFetchedRef.current) return;
    const seeded = [...fullySeededRef.current];
    if (seeded.length === 0) return;
    initTraces(seeded, tracePositions);
  }, [tracePositions, initTraces]);

  // El mapa declaró un viewport nuevo (pan, zoom, filtro): sembrar la cola de
  // los que entraron y todavía no la tienen. Sin esto, panear a una zona nueva
  // deja a esos vehículos con el marcador puesto pero sin cola hasta que la WS
  // les entregue `tracePositions` posiciones — minutos, en un vehículo lento.
  useEffect(() => {
    const fleetStore = storeRef.current;
    return fleetStore.subscribeTraceInterest(() => {
      if (!hasFetchedRef.current) return;
      const interest = fleetStore.getTraceInterest();
      if (!interest) return;
      const missing = [...interest].filter(
        (imei) =>
          !fullySeededRef.current.has(imei) &&
          !pendingSeedRef.current.has(imei),
      );
      if (missing.length === 0) return;
      seedTrailsFor(missing, tracePositionsRef.current);
    });
  }, [seedTrailsFor]);

  // ── Position WS: live vs replay ──────────────────────────────────────────
  // Última posición replayed (is_live:false) pendiente de aplicar, por imei,
  // más los timers del flush diferido. Se limpian al llegar una posición live
  // del mismo device o al desmontar.
  const pendingReplayRef = useRef(new Map());
  const replayFlushTimersRef = useRef(new Map());

  const clearPendingReplay = useCallback((imei) => {
    const timer = replayFlushTimersRef.current.get(imei);
    if (timer !== undefined) {
      clearTimeout(timer);
      replayFlushTimersRef.current.delete(imei);
    }
    pendingReplayRef.current.delete(imei);
  }, []);

  /**
   * Campos de estado/telemetría de un mensaje `position`. Se aplican SIEMPRE,
   * incluso en paquetes replay u out-of-order: no describen el paquete sino la
   * fila `devices` tal como quedó persistida (positionHandler los devuelve del
   * RETURNING, y un paquete retrógrado ya no la modifica), así que son el
   * estado autoritativo del device en el momento del broadcast. Omitirlos
   * congelaba el panel en el estado anterior mientras la ignición sí seguía
   * avanzando — de ahí el "Estacionado hace 5 h" de una moto rodando y el "En
   * movimiento + motor apagado" de una ya parqueada.
   */
  function buildTelemetryPatch(msg) {
    return {
      ...(msg.movement_status != null && {
        movement_status: msg.movement_status,
        stopped_since: msg.stopped_since ?? null,
        parked_since: msg.parked_since ?? null,
      }),
      ...(msg.satellites != null && { satellites: msg.satellites }),
      ...(msg.ignition != null && { ignition: msg.ignition }),
      ...(msg.charging != null && { charging: msg.charging }),
      ...(msg.voltage_level != null && { voltage_level: msg.voltage_level }),
      ...(msg.signal_bars != null && { signal_bars: msg.signal_bars }),
      ...(msg.mcc != null && { mcc: msg.mcc }),
      ...(msg.mnc != null && { mnc: msg.mnc }),
      ...(msg.lac != null && { lac: msg.lac }),
      ...(msg.cell_id != null && { cell_id: msg.cell_id }),
      ...(msg.last_seen_at != null && { last_seen_at: msg.last_seen_at }),
      ...(msg.last_ignition_at != null && {
        last_ignition_at: msg.last_ignition_at,
      }),
      ...(msg.last_charging_at != null && {
        last_charging_at: msg.last_charging_at,
      }),
      ...(msg.total_distance_km != null && {
        total_distance_km: msg.total_distance_km,
      }),
      ...(msg.total_distance_since != null && {
        total_distance_since: msg.total_distance_since,
      }),
      status: "online",
    };
  }

  // Aplica SOLO los campos de estado/telemetría de un mensaje position —
  // nunca la posición. Usado para replays de buffer y paquetes out-of-order:
  // mantiene batería/señal/estado frescos sin mover el marcador hacia atrás
  // ni desordenar el trail.
  const applyPositionTelemetry = useCallback((msg) => {
    const patch = buildTelemetryPatch(msg);
    if (msg.has_gps_fix != null) patch.has_gps_fix = msg.has_gps_fix;
    storeRef.current.patchDevice(msg.imei, patch);
  }, []);

  // Aplica una posición confirmada como nueva-más-reciente: device + trace.
  // Si el salto respecto al punto más nuevo visible es grande (gap GPS
  // > SYNC_LIVE_GAP_THRESHOLD_MS y distancia ≥ SYNC_DISTANCE_THRESHOLD_M) se
  // dispara un syncRequest (el ícono viaja directo al destino vía runSync, sin
  // dibujar línea) y el trail se colapsa al punto de destino. A diferencia del
  // resync de handleWsReconnect, acá NO hay re-fetch: del tramo del gap no
  // tenemos posiciones, así que no hay trail real que mostrar — el trail
  // re-arranca desde el destino.
  const applyPositionUpdate = useCallback((msg) => {
    const fleetStore = storeRef.current;
    const cur = fleetStore.getTrace(msg.imei)[0];
    const curTs = cur?.timestamp ? Date.parse(cur.timestamp) : NaN;
    const msgTs = msg.timestamp ? Date.parse(msg.timestamp) : NaN;
    const jumpSync =
      cur != null &&
      Number.isFinite(cur.lat) &&
      Number.isFinite(cur.lng) &&
      Number.isFinite(msg.lat) &&
      Number.isFinite(msg.lng) &&
      Number.isFinite(curTs) &&
      Number.isFinite(msgTs) &&
      msgTs - curTs > SYNC_LIVE_GAP_THRESHOLD_MS &&
      haversine(cur.lat, cur.lng, msg.lat, msg.lng) >= SYNC_DISTANCE_THRESHOLD_M;

    const newPoint = {
      lat: msg.lat,
      lng: msg.lng,
      timestamp: msg.timestamp,
      heading: msg.heading,
      speed: msg.speed ?? 0,
      movement_status: msg.movement_status ?? "unknown",
    };

    fleetStore.batch(() => {
      // Update device with latest position + telemetry immediately
      fleetStore.patchDevice(msg.imei, {
        ...buildTelemetryPatch(msg),
        lat: msg.lat,
        lng: msg.lng,
        speed: msg.speed,
        heading: msg.heading,
        last_position_at: msg.timestamp,
        gps_received_at:
          msg.last_position_at ??
          fleetStore.getDevice(msg.imei)?.gps_received_at,
        has_gps_fix: msg.has_gps_fix ?? true,
      });

      if (jumpSync) {
        fleetStore.setSyncRequest(msg.imei, {
          from: { lat: cur.lat, lng: cur.lng },
          to: { lat: msg.lat, lng: msg.lng },
          fromTs: cur.timestamp,
          toTs: msg.timestamp,
          reason: "live-gap",
        });
        // Colapsar el trail al punto de destino: el tramo del gap no se dibuja
        // y el trail re-arranca desde la posición nueva en adelante.
        fleetStore.setTrace(msg.imei, [newPoint]);
        return;
      }

      fleetStore.updateTrace(msg.imei, (existing) => {
        // Hard cap: never let traces grow unbounded even if a corrupt/legacy
        // caller hands us 0/NaN. UI clamps to [1, 10] via `clampTrail`; this
        // is defense-in-depth against a future caller bypassing useSettings.
        const TRAIL_HARD_CAP = 200;
        const raw = tracePositionsRef.current;
        const maxCount =
          Number.isFinite(raw) && raw > 0
            ? Math.min(raw, TRAIL_HARD_CAP)
            : TRAIL_HARD_CAP;
        // Dedup: if WS delivers a timestamp we already have (e.g. reconnect
        // resync brought it first), skip.
        if (existing.some((p) => p.timestamp === newPoint.timestamp)) {
          return existing;
        }
        // Orden: el prepend asume que newPoint es el más nuevo. El dispatcher
        // ya lo pre-valida, pero un resync pudo aterrizar entre medio.
        if (
          existing[0]?.timestamp &&
          newPoint.timestamp &&
          Date.parse(existing[0].timestamp) > Date.parse(newPoint.timestamp)
        ) {
          return existing;
        }
        // Heartbeat en el MISMO lugar (< SAME_LOC, ~11 m) que el punto más
        // nuevo: refrescar ese punto en vez de prepend. Un vehículo estacionado
        // sigue reportando la misma coordenada, y con la cola en 5 posiciones
        // (default) cada heartbeat expulsaba un punto real de la ruta: a los
        // minutos de estacionarse el trail eran 5 copias del mismo punto y el
        // tramo de llegada desaparecía.
        //
        // Las COORDENADAS del head se conservan y solo se refresca el resto
        // (timestamp, telemetría). Es deliberado: el ancla tiene que quedarse
        // quieta. Si se pisaran las coordenadas, la referencia de comparación
        // se movería con cada reporte y la deriva acumularía sin techo — un
        // vehículo avanzando ~8 m por reporte (trancón, ~1.4 km/h) nunca
        // superaría el umbral contra su propio punto anterior y el trail
        // dejaría de crecer mientras el marcador se desplaza. Con el ancla fija
        // la deriva está acotada a SAME_LOC.
        const head = existing[0];
        const sameSpot =
          head &&
          Number.isFinite(head.lat) &&
          Number.isFinite(head.lng) &&
          Number.isFinite(newPoint.lat) &&
          Number.isFinite(newPoint.lng) &&
          Math.abs(newPoint.lat - head.lat) < SAME_LOC &&
          Math.abs(newPoint.lng - head.lng) < SAME_LOC;
        const combined = sameSpot
          ? [{ ...newPoint, lat: head.lat, lng: head.lng }, ...existing.slice(1)]
          : [newPoint, ...existing];
        return combined.slice(0, maxCount);
      });
    });
  }, []);

  // Flush diferido del replay: si tras REPLAY_FLUSH_DELAY_MS no llegó nada más
  // nuevo del device, aplica la última posición replayed para que el marcador
  // no quede congelado en la posición pre-desconexión.
  const flushPendingReplay = useCallback(
    (imei) => {
      replayFlushTimersRef.current.delete(imei);
      const msg = pendingReplayRef.current.get(imei);
      pendingReplayRef.current.delete(imei);
      if (!msg) return;
      // Guard: pudo llegar algo más nuevo por otra vía (posición live, resync
      // HTTP de initTraces) mientras el timer corría.
      const cur = storeRef.current.getTrace(imei)[0];
      const curTs = cur?.timestamp ? Date.parse(cur.timestamp) : NaN;
      const newestKnownTs = maxFiniteTs(
        curTs,
        latestAppliedPosTsRef.current.get(imei),
      );
      const msgTs = msg.timestamp ? Date.parse(msg.timestamp) : NaN;
      if (
        Number.isFinite(newestKnownTs) &&
        Number.isFinite(msgTs) &&
        msgTs <= newestKnownTs
      )
        return;
      if (Number.isFinite(msgTs))
        latestAppliedPosTsRef.current.set(imei, msgTs);
      applyPositionUpdate(msg);
    },
    [applyPositionUpdate],
  );

  const handleWsMessage = useCallback(
    (msg) => {
      const fleetStore = storeRef.current;
      // Momento de LLEGADA del mensaje al socket, estampado por el coalescer de
      // WebSocketContext. La entrega (este handler) puede correr hasta ~1 min
      // después con la pestaña oculta: versionar con Date.now() de la entrega
      // invertía el version-guard del resync (estampas > startTs para mensajes
      // anteriores al refetch) y hacía ver silencio falso al watchdog.
      const receivedAt = msg.__wsReceivedAt ?? Date.now();

      // Track WS activity for the silence watchdog. Any incoming message
      // (of any type) resets the clock and stops the safety polling fallback.
      // We inline the stop here (rather than calling stopPolling()) to avoid a
      // forward-reference TDZ — stopPolling is declared later in the hook body.
      lastWsActivityAtRef.current = receivedAt;
      if (pollingActiveRef.current) {
        if (pollIntervalRef.current !== null) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        // Abort any in-flight poll so it doesn't overwrite the fresh WS state.
        currentPollController.current?.abort();
        currentPollController.current = null;
        pollingActiveRef.current = false;
      }

      if (msg.type === "position") {
        // Stamp WS version so the initial fetch knows this device received a
        // real-time update while the HTTP request was in flight.
        if (msg.imei) {
          deviceWsVersionRef.current.set(msg.imei, receivedAt);
          // `position` siempre trae el estado persistido (ver positionHandler),
          // incluso en tramas replay u out-of-order.
          if (msg.movement_status != null)
            deviceMovementWsVersionRef.current.set(msg.imei, receivedAt);
        }

        // Guard de fuera-de-orden. El store es síncrono, así que la cabeza ya
        // refleja los mensajes anteriores del mismo lote coalescido;
        // latestAppliedPosTsRef cubre además el punto de replay que todavía no
        // llegó al store. Se toma el máximo de ambas fuentes.
        const cur = fleetStore.getTrace(msg.imei)[0];
        const curTs = cur?.timestamp ? Date.parse(cur.timestamp) : NaN;
        const newestKnownTs = maxFiniteTs(
          curTs,
          latestAppliedPosTsRef.current.get(msg.imei),
        );
        const msgTs = msg.timestamp ? Date.parse(msg.timestamp) : NaN;
        const isStale =
          Number.isFinite(newestKnownTs) &&
          Number.isFinite(msgTs) &&
          msgTs <= newestKnownTs;

        if (isStale) {
          // Retransmisión o entrega out-of-order (timestamp GPS no más nuevo que
          // el punto visible): nunca mover el marcador hacia atrás ni desordenar
          // el trail — el prepend asume orden cronológico de llegada.
          applyPositionTelemetry(msg);
        } else if (msg.is_live === false && cur) {
          // Replay de buffer offline (device-side resync): recordar la más
          // reciente y aplicar solo telemetría. El flush diferido cubre el caso
          // "replay sin posición live después" (device se duerme tras el dump).
          pendingReplayRef.current.set(msg.imei, msg);
          const prevTimer = replayFlushTimersRef.current.get(msg.imei);
          if (prevTimer !== undefined) clearTimeout(prevTimer);
          replayFlushTimersRef.current.set(
            msg.imei,
            setTimeout(() => flushPendingReplay(msg.imei), REPLAY_FLUSH_DELAY_MS),
          );
          applyPositionTelemetry(msg);
        } else {
          // Posición live (o primer punto conocido del device) — superseda
          // cualquier replay pendiente: el destino live ya cubre ese recorrido.
          clearPendingReplay(msg.imei);
          if (Number.isFinite(msgTs))
            latestAppliedPosTsRef.current.set(msg.imei, msgTs);
          applyPositionUpdate(msg);
        }
      }

      if (msg.type === "telemetry") {
        if (msg.imei) deviceWsVersionRef.current.set(msg.imei, receivedAt);
        fleetStore.patchDevice(msg.imei, {
          ...(msg.voltage_level != null && { voltage_level: msg.voltage_level }),
          ...(msg.signal_bars != null && { signal_bars: msg.signal_bars }),
          ...(msg.ignition != null && { ignition: msg.ignition }),
          ...(msg.status != null && { status: msg.status }),
          ...(msg.last_seen_at != null && { last_seen_at: msg.last_seen_at }),
          ...(msg.relay_status != null && { relay_status: msg.relay_status }),
          ...(msg.has_gps_fix != null && { has_gps_fix: msg.has_gps_fix }),
          ...(msg.satellites != null && { satellites: msg.satellites }),
          ...(msg.charging != null && { charging: msg.charging }),
          ...(msg.mcc != null && { mcc: msg.mcc }),
          ...(msg.mnc != null && { mnc: msg.mnc }),
          ...(msg.lac != null && { lac: msg.lac }),
          ...(msg.cell_id != null && { cell_id: msg.cell_id }),
          ...(msg.last_ignition_at != null && {
            last_ignition_at: msg.last_ignition_at,
          }),
          ...(msg.last_charging_at != null && {
            last_charging_at: msg.last_charging_at,
          }),
        });
      }

      if (msg.type === "device_movement") {
        if (msg.imei) {
          deviceWsVersionRef.current.set(msg.imei, receivedAt);
          deviceMovementWsVersionRef.current.set(msg.imei, receivedAt);
        }
        fleetStore.patchDevice(msg.imei, {
          movement_status: msg.movement_status,
          ...(msg.stopped_since !== undefined && {
            stopped_since: msg.stopped_since,
          }),
          ...(msg.parked_since !== undefined && {
            parked_since: msg.parked_since,
          }),
        });
      }

      // Estado administrativo del vehículo, cambiado desde /admin. No es
      // telemetría: no lleva version-guard contra los refs de WS porque no
      // compite con ningún mensaje del equipo, y `patchDevice` ya recoloca los
      // contadores por estado.
      //
      // Robado = fuera de la app (salvo /admin → Vehículos): se suelta del
      // store en el acto. Y al revés, un vehículo que deja de estar robado no
      // está en el store para parchearlo: hay que traerlo con un resync, que
      // además le siembra la traza como a cualquier otro.
      if (msg.type === "vehicle_status" && msg.imei) {
        const nextStatus = msg.vehicle_status ?? null;
        if (isHiddenVehicleStatus(nextStatus)) {
          fleetStore.removeDevice(msg.imei);
        } else if (fleetStore.getDevice(msg.imei)) {
          fleetStore.patchDevice(msg.imei, { vehicle_status: nextStatus });
        } else if (initialFetchSettledRef.current) {
          resyncRef.current?.("vehicle-visible");
        }
      }

      // Equipo retirado (dañado/perdido) o borrado del inventario: deja de
      // existir para el mapa. Sin esto seguía pintado como "Desconectado"
      // hasta recargar la página.
      if (msg.type === "device_removed" && msg.imei) {
        fleetStore.removeDevice(msg.imei);
      }

      if (msg.type === "device_status") {
        // Versión ACOTADA a status — no la general: la general protege todo el
        // grupo de telemetría en los merges (un status durante el refetch
        // preservaba batería/ignición/odómetro viejos), pero sin ninguna versión
        // un status emitido con el fetch inicial en vuelo era pisado por el
        // snapshot viejo y un device offline no re-emite para corregirlo.
        if (msg.imei) deviceStatusWsVersionRef.current.set(msg.imei, receivedAt);
        const known = fleetStore.getDevice(msg.imei);
        if (known) {
          fleetStore.patchDevice(msg.imei, { status: msg.status });
        } else if (!initialFetchSettledRef.current) {
          // IMEI desconocido. Antes se agregaba una fila {imei, name: imei}
          // siempre, y eso convertía cualquier device_status que no fuera de
          // este usuario en una fila fantasma en su lista de vehículos
          // (enumeración de IMEIs ajenos si el fan-out del backend fallaba en
          // scopear). Defensa en profundidad: la lista de devices sale del
          // fetch REST, que es el único camino con filtro de visibilidad; el WS
          // solo parchea lo que ya está.
          //
          // Excepción: antes de que resuelva el fetch inicial la lista está
          // vacía y perder el evento sí importa (race REST/WS del issue #115).
          // Esa fila provisional la reconcilia el propio fetch — su merge parte
          // de la respuesta HTTP, así que un IMEI que el usuario no puede ver
          // desaparece en cuanto llega el snapshot.
          fleetStore.upsertDevice({
            imei: msg.imei,
            name: msg.imei,
            status: msg.status,
          });
        }
      }

      // Sonda STATUS# que la plataforma manda sola al sospechar un corte de
      // batería (relay_commands.origin = 'system'). Hay UN comando por vehículo
      // en el store y el Monitor sigue por commandId el que mandó el operador:
      // mientras ese comando esté vivo (CREATED/SENT), la sonda no lo pisa —
      // si lo hiciera justo durante un robo, el CORTAR nunca volvería a verse
      // y ni "Combustible cortado" ni "no confirmó" se avisarían. Sin comando
      // del operador en vuelo la sonda sí entra, para que el chat de comandos
      // la muestre en vivo con su respuesta.
      if (msg.type === "command_update" && msg.imei && !systemUpdateWouldHideOperatorCommand(fleetStore, msg)) {
        fleetStore.batch(() => {
          fleetStore.setCommand(msg.imei, {
            commandId: msg.commandId,
            state: msg.state,
            updatedAt: Date.now(),
            response_content: msg.response_content ?? null,
            // LOCK/UNLOCK/STATUS/CUSTOM — el Monitor lo usa para togglear solo
            // los toasts de relé. El sobre WS ya ocupa `type` con el nombre del
            // mensaje, por eso el tipo del comando viaja como `command_type`.
            command_type: msg.command_type ?? null,
            // Un CUSTOM con payload `Relay,1#` corta combustible igual que un
            // LOCK: el backend lo detecta en la respuesta del equipo y manda
            // relay_status, así que el Monitor lo necesita para reconocerlo.
            relay_status: msg.relay_status ?? null,
            // Cancelado porque el operador mandó otro comando encima, no por
            // entrega fallida — el Monitor no debe avisarlo como fallo. Desde
            // db/migrations/106 eso llega como state='CANCELLED'; la marca se
            // sigue leyendo para los FAILED que manda un backend sin actualizar.
            superseded: msg.superseded === true,
            // 'user' | 'system' (sonda automática). Un backend viejo no lo manda:
            // se asume del operador, que es lo que siempre fue.
            origin: msg.origin ?? "user",
          });
          if (msg.relay_status != null) {
            fleetStore.patchDevice(msg.imei, { relay_status: msg.relay_status });
          }
        });
      }

      if (msg.type === "geofence_event") {
        const alertId = `${Date.now()}-${Math.random()}`;
        // Cap: una geocerca grande puede generar decenas de ENTER/EXIT en una
        // ráfaga; sin tope el array crecía más rápido de lo que el timer de 5s
        // drenaba. Se conservan las más recientes (los timers viejos siguen
        // corriendo y su filter por id es inocuo si la alerta ya fue expulsada).
        fleetStore.pushGeofenceAlert(
          {
            id: alertId,
            event_type: msg.event_type,
            geofence_name: msg.geofence_name,
            imei: msg.imei,
            occurred_at: msg.occurred_at,
          },
          MAX_GEOFENCE_ALERTS,
        );
        const handle = setTimeout(() => {
          storeRef.current?.removeGeofenceAlert(alertId);
          pendingGeofenceTimers.current.delete(handle);
        }, 5000);
        pendingGeofenceTimers.current.add(handle);
      }
    },
    [
      applyPositionTelemetry,
      applyPositionUpdate,
      flushPendingReplay,
      clearPendingReplay,
    ],
  );

  // Stable ref to the absence-reader from WebSocketContext. Populated below
  // after useWebSocket() runs. handleWsReconnect closes over the ref so it
  // does not need the value at declaration time.
  const getAbsenceMsRef = useRef(null);

  // Single-flight del resync: controller del vuelo en curso + secuencia
  // monótona. Un resync nuevo aborta al anterior y se vuelve el único dueño
  // del estado (roster y siembras).
  const resyncControllerRef = useRef(null);
  // `getAndConsumeLastAbsenceMs` es read-and-clear: el primer vuelo que la lee
  // la borra. Con single-flight eso abre un agujero — el vuelo que la consumió
  // puede ser abortado por uno más nuevo, y el nuevo leería null. Secuencia
  // real: pestaña oculta 20 min → resync "tab-return" consume la ausencia → el
  // heartbeat no recibe pong y fuerza reconexión → su "reconnect" aborta al
  // primero. Sin este relevo, evaluateSyncTrigger no ve la ausencia y los
  // marcadores saltan de golpe en vez de hacer el catch-up animado, que es
  // justo lo que el sync existe para evitar.
  const pendingAbsenceMsRef = useRef(null);

  // Abort del resync en vuelo al desmontar: sin esto un fetch tardío escribe
  // al store de un motor muerto.
  useEffect(() => () => resyncControllerRef.current?.abort(), []);

  // Único punto de entrada del resync. WebSocketProvider lo dispara por dos
  // motivos: "reconnect" (el socket se cayó y volvió) y "tab-return" (la
  // pestaña volvió tras una ausencia larga con el socket sano).
  //
  // Ninguno de los dos BLOQUEA. Históricamente "reconnect" levantaba el
  // overlay fullscreen "Sincronizando / Reconectando al servidor"; en la app
  // nativa el socket muere en CADA vuelta del background (el OS congela el
  // proceso), así que aquel overlay convertía cada regreso en una pantalla
  // bloqueante de varios segundos. El refetch corre en silencio sobre el
  // último estado conocido, y el catch-up lo comunica la animación de sync
  // del marcador — que el blur del overlay tapaba, justamente.
  const handleWsReconnect = useCallback(
    (reason) => {
      const fleetStore = storeRef.current;
      // Vuelo único: el resync nuevo aborta al anterior en el acto. Sin esto,
      // dos vuelos solapados (probe stale-OPEN + onopen, reconexiones en
      // ráfaga) publican sus rosters en orden de llegada — el que pierde la
      // carrera pisa al fresco.
      resyncControllerRef.current?.abort();
      const controller = new AbortController();
      resyncControllerRef.current = controller;
      // Dueño del estado = el controller vigente. Una secuencia numérica en
      // paralelo sería una segunda fuente de verdad para la misma pregunta, y
      // un abort futuro que se olvidara de incrementarla dejaría a un vuelo
      // muerto creyéndose vigente.
      const isCurrent = () => controller === resyncControllerRef.current;
      const startTs = Date.now();

      // Snapshot pre-reconnect traces and consume absenceMs ONCE up front.
      // Both inputs feed evaluateSyncTrigger after initTraces merges fresh data.
      const prevTraces = fleetStore.getTracesObject();
      // Préstamo de la ausencia, no relevo diferido. `getAndConsumeLastAbsenceMs`
      // es read-and-clear, así que el vuelo que la lee se la lleva; si después
      // lo abortan, la ausencia se perdería. El ref se reclama ACÁ, en el mismo
      // bloque síncrono que el abort() de arriba — devolverla desde un `finally`
      // no funciona: ese `finally` corre en un microtask posterior y el vuelo
      // que superseda ya leyó el ref (y se llevaría null). Se limpia recién
      // cuando la evaluación de sync la usa de verdad.
      const absenceMs =
        pendingAbsenceMsRef.current ??
        (typeof getAbsenceMsRef.current === "function"
          ? getAbsenceMsRef.current()
          : null);
      pendingAbsenceMsRef.current = absenceMs;
      wsLog("start", { absenceMs, reason: reason ?? "reconnect" });

      return (async () => {
        try {
          const deviceList = await getDevicesWithRetry(
            (opts) => api.getDevices(opts),
            { signal: controller.signal },
          );
          // El fetch pudo completar en la misma vuelta de microtasks en que un
          // vuelo más nuevo abortó este: el estado le pertenece al nuevo (la
          // ausencia se la devuelve el `finally`).
          if (!isCurrent()) return;
          trackUpdatedAt(deviceList);
          wsLog("devices-refetch ok", { count: deviceList.length });
          // El backend ya no sirve los robados en el snapshot completo; el
          // filtro repetido acá es por si un backend viejo los mandara.
          const devicesWithoutPos = deviceList
            .filter((d) => !isHiddenVehicleStatus(d.vehicle_status))
            .map(
              // eslint-disable-next-line no-unused-vars
              ({ lat, lng, last_position_at, ...rest }) => rest,
            );

          // Campos que `devicesWithoutPos` stripea del snapshot HTTP: no hay
          // valor fresco con el que reemplazarlos, así que se preservan SIEMPRE
          // o el marcador se queda sin coordenadas. Sólo estos tres — el resto
          // de /api/devices sí viene completo y va abajo, bajo el version-guard.
          const RT_FIELDS_STRIPPED_FROM_HTTP = [
            "lat",
            "lng",
            "last_position_at",
          ];
          // Campos de ESTADO del device. /api/devices los trae autoritativos
          // desde la tabla `devices`, así que preservar la copia en memoria solo
          // es correcto si esa copia es MÁS NUEVA que el snapshot HTTP — o sea,
          // si llegó un mensaje WS después de arrancar el refetch.
          //
          // Sin este guard, el reconnect reinstalaba el estado previo a la
          // desconexión justo cuando es el más viejo posible: una moto que se
          // durmió la pestaña estando 'parked' seguía mostrando ese parked_since
          // (contador que nunca se reinicia) y una que estaba 'moving' volvía
          // como "En movimiento" mientras `ignition`, que sí venía fresco del
          // HTTP, decía motor apagado.
          //
          // speed/heading/satellites van ACÁ y no arriba: initTraces sí re-deriva
          // lat/lng/last_position_at, pero NO speed/heading, así que un `speed`
          // preservado de memoria no lo refresca nadie. O todo el snapshot viene
          // del HTTP, o todo viene de memoria.
          //
          // Se guardan con DOS versiones distintas según qué mensaje WS los
          // refresca de verdad. Un `telemetry` de heartbeat no dice nada del
          // movimiento ni de la velocidad; si fijara esos campos, un heartbeat
          // caído dentro de la ventana del refetch reinstalaría justo el estado
          // viejo que este guard vino a descartar (y estos trackers emiten cada
          // ~1-2.5 min, así que pasaría seguido).
          const RT_FIELDS_FROM_MOVEMENT = [
            "movement_status",
            "stopped_since",
            "parked_since",
            "speed",
            "heading",
          ];
          const RT_FIELDS_FROM_TELEMETRY = [
            "satellites",
            "gps_received_at",
            "has_gps_fix",
            "voltage_level",
            "signal_bars",
            "last_seen_at",
            // ignition/charging viajan acá y NO se dejan siempre al HTTP: si una
            // posición WS más nueva que el snapshot trae "moving + motor
            // encendido", pisar sólo la ignición con el HTTP viejo devuelve
            // exactamente el par contradictorio que este branch ataca. `status`
            // y `relay_status` quedan afuera a propósito: ahí el HTTP siempre es
            // autoritativo.
            "ignition",
            "charging",
            "last_ignition_at",
            "last_charging_at",
            // Odómetro: mismo criterio. Sin mensaje WS más nuevo gana el valor
            // de /api/devices (creció mientras la WS estuvo caída); con uno más
            // nuevo gana la memoria para no retroceder.
            "total_distance_km",
            "total_distance_since",
          ];

          fleetStore.setRoster(devicesWithoutPos, (fresh, existing) => {
            if (!existing) return fresh;
            const merged = { ...fresh };
            for (const k of RT_FIELDS_STRIPPED_FROM_HTTP) {
              if (existing[k] !== undefined) merged[k] = existing[k];
            }
            // ¿Llegó por WS algo más nuevo que el snapshot HTTP? Se pregunta por
            // separado para cada grupo: sólo gana la memoria si el mensaje que
            // llegó realmente traía esos campos.
            const isNewer = (ref) => {
              const v = ref.current.get(fresh.imei);
              return v !== undefined && v >= startTs;
            };
            if (isNewer(deviceMovementWsVersionRef)) {
              for (const k of RT_FIELDS_FROM_MOVEMENT) {
                if (existing[k] !== undefined) merged[k] = existing[k];
              }
            }
            if (isNewer(deviceWsVersionRef)) {
              for (const k of RT_FIELDS_FROM_TELEMETRY) {
                if (existing[k] !== undefined) merged[k] = existing[k];
              }
            }
            return merged;
          });

          await seedAfterResync(
            devicesWithoutPos.map((d) => d.imei),
            // Inspecciona sincrónicamente el mapa mezclado y emite syncRequests
            // para los devices que cumplen el criterio. Si falta la traza previa
            // O la nueva → sin request, se sigue.
            //
            // Si ya existe un syncRequest para un imei (un sync anterior sin
            // resolver) el store MEZCLA en vez de reemplazar — conserva el
            // from/fromTs original (lo que el usuario vio) y solo avanza el
            // destino.
            //
            // Devolver un mapa ajustado desde este callback REEMPLAZA las trazas
            // mezcladas: para cada device que dispara sync, la traza se colapsa
            // SOLO al punto de destino. Ese es el contrato entero de un sync —
            // ponerse al día sin inventar un recorrido: el marcador se desliza
            // hasta el punto actual sin dibujar nada (VehicleMotion.runSync) y
            // el trail re-arranca desde ahí. Conservar la cola re-fetcheada lo
            // contradice: el usuario nunca vio ese tramo, y lo que queda pintado
            // se lee como una línea de vuelta a donde estaba el vehículo cuando
            // cambió de pestaña.
            ({ next: nextTraces }) => {
              // Consumida de verdad: dejar de prestársela al próximo vuelo.
              pendingAbsenceMsRef.current = null;
              const triggers = []; // [{imei, prev, next, reason}]
              for (const device of devicesWithoutPos) {
                const prevForDevice = prevTraces[device.imei];
                const nextForDevice = nextTraces[device.imei];
                if (!prevForDevice?.length || !nextForDevice?.length) continue;
                const prev = prevForDevice[0]; // newest-first ordering
                const next = nextForDevice[0];
                if (
                  !Number.isFinite(prev?.lat) ||
                  !Number.isFinite(prev?.lng) ||
                  !Number.isFinite(next?.lat) ||
                  !Number.isFinite(next?.lng)
                )
                  continue;
                // Skip if WS already covered this update (same timestamp on top).
                if (prev.timestamp && prev.timestamp === next.timestamp) continue;
                const trigger = evaluateSyncTrigger({
                  from: [prev.lat, prev.lng],
                  to: [next.lat, next.lng],
                  fromTs: prev.timestamp ? Date.parse(prev.timestamp) : null,
                  toTs: next.timestamp ? Date.parse(next.timestamp) : null,
                  absenceMs,
                  queueLength: 0,
                });
                if (trigger) {
                  triggers.push({
                    imei: device.imei,
                    prev,
                    next,
                    reason: trigger.reason,
                  });
                }
              }
              if (triggers.length === 0) return undefined;
              fleetStore.batch(() => {
                for (const { imei, prev, next, reason } of triggers) {
                  fleetStore.setSyncRequest(imei, {
                    from: { lat: prev.lat, lng: prev.lng },
                    to: { lat: next.lat, lng: next.lng },
                    fromTs: prev.timestamp,
                    toTs: next.timestamp,
                    reason,
                  });
                }
              });
              const adjusted = { ...nextTraces };
              for (const { imei, next } of triggers) {
                adjusted[imei] = [next];
              }
              return adjusted;
            },
            { signal: controller.signal },
          );
        } catch (err) {
          if (controller.signal.aborted) {
            // Cancelado (superseded por un resync más nuevo, o unmount): no es
            // un fallo, y el estado ya no es de este vuelo.
            wsLog("resync aborted", {
              reason: reason ?? "reconnect",
              superseded: !isCurrent(),
            });
            return;
          }
          wsLog("devices-refetch fail", { error: err?.message ?? String(err) });
          console.error("[fleetEngine] reconnect re-fetch failed:", err);
        } finally {
          wsLog("done", {
            ms: Date.now() - startTs,
            superseded: !isCurrent(),
            absenceStillPending: pendingAbsenceMsRef.current,
          });
        }
      })();
    },
    [seedAfterResync, trackUpdatedAt],
  );

  const { connected: wsConnected, getAndConsumeLastAbsenceMs, isSocketOpen } =
    useWebSocket(handleWsMessage, { onReconnect: handleWsReconnect });

  // Keep the absence reader available to handleWsReconnect via ref so the
  // callback identity stays stable across renders.
  useEffect(() => {
    getAbsenceMsRef.current = getAndConsumeLastAbsenceMs ?? null;
  }, [getAndConsumeLastAbsenceMs]);

  useEffect(() => {
    resyncRef.current = handleWsReconnect;
  }, [handleWsReconnect]);

  // Estado del socket espejado al store, para consumidores que solo ven el
  // store (hoy, la vista legacy de tests — AppChrome lo lee directo del
  // contexto WS). Si el motor lo devolviera como valor de retorno, el
  // componente que lo corre se re-renderizaría al conectar/desconectar — y ese
  // componente es el provider, o sea toda la app.
  useEffect(() => {
    storeRef.current.setStatus({ wsConnected, isSocketOpen: isSocketOpen ?? null });
  }, [wsConnected, isSocketOpen]);

  // ── Safety polling helpers ────────────────────────────────────────────────

  /**
   * Run a single safety poll: fetch GET /api/devices and merge POLL_FIELDS
   * into the store, skipping any device where a newer WS message arrived since
   * the poll started (version guard via deviceWsVersionRef).
   * No-ops when the tab is hidden.
   */
  const runPoll = useCallback(async (signal) => {
    if (document.hidden) return;
    const pollStartedAt = Date.now();
    let deviceList;
    try {
      // Delta: solo devices con cambios desde el último updated_at visto. En
      // el primer poll tras un rato de WS sana el delta viene "grande" (el
      // reloj solo avanza con respuestas HTTP), pero la misma respuesta lo
      // adelanta y los siguientes ticks quedan chicos o vacíos. El merge de
      // abajo ya es delta-tolerante: ausente en la respuesta = sin cambios.
      const since =
        maxUpdatedAtRef.current != null
          ? new Date(maxUpdatedAtRef.current - DELTA_OVERLAP_MS).toISOString()
          : undefined;
      deviceList = await api.getDevices(since ? { signal, since } : { signal });
    } catch (err) {
      if (err.name === "AbortError") return; // clean cancellation — no log, no state update
      console.error("[fleetEngine] safety poll failed:", err);
      return;
    }
    trackUpdatedAt(deviceList);
    const fleetStore = storeRef.current;
    fleetStore.batch(() => {
      for (const fresh of deviceList) {
        if (!fleetStore.getDevice(fresh.imei)) continue;
        // El delta trae al robado a propósito (ver GET /api/devices): es como
        // se entera el store de que tiene que soltarlo si el aviso WS se perdió.
        if (isHiddenVehicleStatus(fresh.vehicle_status)) {
          fleetStore.removeDevice(fresh.imei);
          continue;
        }
        // If a WS message arrived AFTER the poll started, its data is fresher —
        // skip this device entirely so we don't overwrite WS state with the
        // (possibly older) poll snapshot.
        const wsVersion = deviceWsVersionRef.current.get(fresh.imei);
        if (wsVersion !== undefined && wsVersion >= pollStartedAt) continue;
        // Merge only the POLL_FIELDS subset; position/trace fields are untouched.
        const patch = {};
        for (const k of POLL_FIELDS) {
          if (k in fresh) patch[k] = fresh[k];
        }
        fleetStore.patchDevice(fresh.imei, patch);
      }
    });
  }, [trackUpdatedAt]);

  /** Stop the safety polling interval if it's running. */
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    // Abort any in-flight poll request so it doesn't mutate the store after stop.
    currentPollController.current?.abort();
    currentPollController.current = null;
    pollingActiveRef.current = false;
  }, []);

  /** Start the safety polling interval if not already running. */
  const startPolling = useCallback(() => {
    if (pollingActiveRef.current) return;
    pollingActiveRef.current = true;
    // Fire once immediately, then on each interval.
    // Invariant: each tick overwrites currentPollController, orphaning any
    // still-in-flight prior poll. This is safe only because POLL_INTERVAL_MS
    // (30s) > DEFAULT_TIMEOUT_MS (10s) — every poll aborts/times-out before the
    // next tick. Keep that ordering if either constant changes.
    currentPollController.current = new AbortController();
    runPoll(currentPollController.current.signal).catch(() => {});
    pollIntervalRef.current = setInterval(() => {
      currentPollController.current = new AbortController();
      runPoll(currentPollController.current.signal).catch(() => {});
    }, POLL_INTERVAL_MS);
  }, [runPoll]);

  // Watchdog: checks every WS_SILENCE_THRESHOLD_MS whether any WS activity
  // has occurred. Starts/stops polling accordingly.
  useEffect(() => {
    watchdogIntervalRef.current = setInterval(() => {
      const silenceMs = Date.now() - lastWsActivityAtRef.current;
      if (silenceMs >= WS_SILENCE_THRESHOLD_MS) {
        startPolling();
      }
    }, WS_SILENCE_THRESHOLD_MS);

    return () => {
      clearInterval(watchdogIntervalRef.current);
      watchdogIntervalRef.current = null;
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  // Clear any pending geofence auto-dismiss + replay-flush timers on unmount
  // so no setTimeout callback fires against an unmounted component.
  useEffect(() => {
    const geofenceTimers = pendingGeofenceTimers.current;
    const replayTimers = replayFlushTimersRef.current;
    const pendingReplays = pendingReplayRef.current;
    return () => {
      for (const h of geofenceTimers) clearTimeout(h);
      geofenceTimers.clear();
      for (const h of replayTimers.values()) clearTimeout(h);
      replayTimers.clear();
      pendingReplays.clear();
    };
  }, []);

  // El motor no devuelve nada: todo lo que produce va al store. Así el
  // componente que lo monta (`<FleetProvider>`) nunca tiene que re-renderizar
  // para propagar un cambio, que es la premisa entera de esta fase.
  //
  // Las acciones que disparan los componentes viven en `lib/fleet/fleetActions.js`.
}

export default useFleetEngine;

// Estados en los que un comando ya no está en vuelo: su command_update
// terminal ya llegó (o nunca va a llegar) y el Monitor dejó de seguirlo.
const TERMINAL_COMMAND_STATES = new Set(["COMPLETED", "FAILED", "CANCELLED", "APPLIED"]);
const OPERATOR_COMMAND_IN_FLIGHT_MS = 2 * 60 * 1000;

/**
 * ¿Este command_update es de la plataforma (sonda de batería) y taparía en el
 * store un comando del operador que sigue vivo? Ver el comentario en el
 * handler de `command_update`.
 */
function systemUpdateWouldHideOperatorCommand(store, msg) {
  if (msg.origin !== "system" && msg.origin !== "schedule") return false;
  const current = store.getCommand(msg.imei);
  if (!current || current.origin === "system" || current.origin === "schedule") return false;
  // Un comando del operador se considera "en vuelo" solo por un rato: el job de
  // timeout lo expira a los 30 s y su command_update terminal puede perderse
  // (WS caído, pestaña oculta). Sin esta cota un SENT huérfano bloquearía
  // las sondas de ese vehículo en el store por el resto de la sesión.
  const ageMs = Date.now() - (Number(current.updatedAt) || 0);
  return !TERMINAL_COMMAND_STATES.has(current.state) && ageMs < OPERATOR_COMMAND_IN_FLIGHT_MS;
}
